import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  Renderer2,
} from '@angular/core';

const DEFAULT_HEADER_SELECTOR = '.header-container';
// The console's version also looked for `.md-virtual-repeat-scroller`, the
// AngularJS Material scroller. Dropped here: this library has no AngularJS.
const DEFAULT_BODY_SELECTORS = ['.body-container', '.cdk-virtual-scroll-viewport'];

/**
 * Keeps a detached table header aligned with the scrolling table body:
 *  - mirrors the body horizontal scroll offset on the header
 *  - reserves the body vertical scrollbar width on the header, so columns stay aligned
 *
 * Place it on the element that contains both the header and the body
 * (the .table-outer-wrapper element), next to [tableColumnManager] on the header.
 */
@Directive({
  selector: '[bzTableScrollSync]'
})
export class BzTableScrollSyncDirective implements AfterViewInit, OnDestroy {

  readonly headerSelector = input(DEFAULT_HEADER_SELECTOR);
  /** Unset falls back to DEFAULT_BODY_SELECTORS, tried in order. */
  readonly bodySelector = input<string | undefined>(undefined);

  private headerEl: HTMLElement | null = null;
  private bodyEl: HTMLElement | null = null;
  private unlistenScroll: (() => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly zone = inject(NgZone);

  ngAfterViewInit(): void {
    this._connect();
    this._observeStructureChanges();
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this._disconnect();
  }

  private _connect(): boolean {
    const host = this.el.nativeElement as HTMLElement;
    const headerEl = host.querySelector(this.headerSelector()) as HTMLElement;
    const bodyEl = this._findBodyElement(host);

    if (!headerEl || !bodyEl) {
      return false;
    }
    if (headerEl === this.headerEl && bodyEl === this.bodyEl) {
      return true;
    }

    this._disconnect();
    this.headerEl = headerEl;
    this.bodyEl = bodyEl;

    // Scrolling only mutates scrollLeft / padding, never a binding - keep it out of change detection
    this.zone.runOutsideAngular(() => {
      this.unlistenScroll = this.renderer.listen(this.bodyEl, 'scroll', () => {
        // The gutter is re-checked here, not only on resize, so a table that was measured
        // while hidden corrects itself the first time anyone touches it.
        this._syncScrollbarGutter();
        this._syncScrollLeft();
      });
      this._observeSizeChanges(bodyEl);
    });

    this._syncScrollLeft();
    this._syncScrollbarGutter();
    return true;
  }

  private _disconnect(): void {
    if (this.unlistenScroll) {
      this.unlistenScroll();
      this.unlistenScroll = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.headerEl = null;
    this.bodyEl = null;
  }

  private _findBodyElement(host: HTMLElement): HTMLElement | null {
    const bodySelector = this.bodySelector();
    const selectors = bodySelector ? [bodySelector] : DEFAULT_BODY_SELECTORS;
    for (const selector of selectors) {
      const found = host.querySelector(selector) as HTMLElement;
      if (found) {
        return found;
      }
    }
    return null;
  }

  /**
   * The body element is often rendered later than the header (*ngIf on an empty list,
   * md-virtual-repeat building its scroller), so watch for it appearing / being replaced.
   * Only direct children are observed - row rendering inside the body must not trigger this.
   */
  private _observeStructureChanges(): void {
    if (typeof MutationObserver === 'undefined') {
      return;
    }
    this.mutationObserver = new MutationObserver((mutations) => {
      const structureChanged = mutations.some((mutation) => mutation.type === 'childList');
      if (structureChanged) {
        this._connect();
      }
      // [hidden] lives on this same host for hideWhenEmpty. That toggle is an attribute
      // mutation, not a childList one, and it is exactly when a 0×0 first measurement
      // becomes a real box.
      this._syncScrollbarGutter();
    });
    this.mutationObserver.observe(this.el.nativeElement, {
      childList: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });
  }

  // Takes the body element as an argument rather than reading `this.bodyEl`: it is only ever
  // called from _connect, which has already proved the element exists, and the compiler cannot
  // carry that knowledge across the call.
  private _observeSizeChanges(bodyEl: HTMLElement): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this._syncScrollbarGutter());
    this.resizeObserver.observe(bodyEl);
    // The host is what carries [hidden] for a `hideWhenEmpty` table, so this is the
    // observation that reports the table gaining a box and lets the skipped first
    // measurement be taken for real.
    this.resizeObserver.observe(this.el.nativeElement);

    const innerWrapper = bodyEl.querySelector('.table-inner-wrapper') as HTMLElement;
    if (innerWrapper) {
      this.resizeObserver.observe(innerWrapper);
    }
  }

  private _syncScrollLeft(): void {
    if (this.headerEl && this.bodyEl) {
      this.headerEl.scrollLeft = this.bodyEl.scrollLeft;
    }
  }

  private _syncScrollbarGutter(): void {
    if (!this.headerEl || !this.bodyEl) {
      return;
    }
    // A table with no box measures 0x0, and 0 is a legitimate-looking gutter - it is what an
    // overlay-scrollbar machine reports. Writing it would therefore latch silently and never
    // be corrected, because the real value only changes with the viewer's OS scrollbar
    // setting. A `hideWhenEmpty` table is hidden exactly when this first runs, so that is the
    // normal path into this state, not an edge case: skip, and let a later sync do it.
    if (!this.bodyEl.offsetWidth) {
      return;
    }
    const gutter = `${this.bodyEl.offsetWidth - this.bodyEl.clientWidth}px`;
    if (this.headerEl.style.paddingRight !== gutter) {
      this.headerEl.style.paddingRight = gutter;
    }
  }
}
